/**
 * Fase 6 Test Component
 * 
 * Comprehensive testing interface for Heatmap Visualisering features.
 * Tests HeatmapCalculator, HeatmapRackGrid, HeatmapLegend, and ModeToggle components.
 * 
 * @author BizClick WMS
 * @version 1.29 - Fase 6 Implementation (Fixed)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ActivityCacheManager, TimeWindow } from './ActivityCacheManager';
import { HeatmapCalculator, LocationIntensity, HeatmapData } from './HeatmapCalculator';
import HeatmapRackGrid from './HeatmapRackGrid';
import LiveRackGrid from './LiveRackGrid';
import HeatmapLegend from './HeatmapLegend';
import ModeToggle from './ModeToggle';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { RackStructure, BinLocation } from '../rack-builder/types';
import { RefreshCw, Zap, TrendingUp } from 'lucide-react';

const Fase6Test: React.FC = () => {
  // State
  const [mode, setMode] = useState<'live' | 'heatmap'>('live');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(TimeWindow.Today);
  const [colorScheme, setColorScheme] = useState<'cold-hot' | 'green-red' | 'grayscale'>('cold-hot');
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [activeActivities, setActiveActivities] = useState<Map<string, LocationActivityEvent[]>>(new Map());
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);

  // Initialize managers
  const cacheManager = useMemo(() => new ActivityCacheManager({
    maxCacheSize: 10000,
    enablePersistence: false, // Disable for testing
    enableMidnightReset: false
  }), []);

  const heatmapCalculator = useMemo(() => new HeatmapCalculator({
    timeWindow: timeWindow,
    normalizationThreshold: 20,
    includeZeroActivity: true
  }), [timeWindow]);

  // Mock rack structure (5 verticals × 6 shelves = 30 locations)
  const mockRackStructure: RackStructure = useMemo(() => {
    const locations: BinLocation[] = [];
    const verticals = [1, 2, 3, 4, 5];
    const shelves = [1, 2, 3, 4, 5, 6];

    verticals.forEach(v => {
      shelves.forEach(s => {
        const binCode = `WH01-R05-V${v.toString().padStart(2, '0')}-S${s.toString().padStart(2, '0')}`;
        locations.push({
          binCode,
          vertical: v,
          shelf: s,
          rackNumber: '5', // String, not number
          binSizeNo: 1,
          binTypeNo: 1,
          pickPriorityNo: 10,
          putAwayPriorityNo: 10,
          itemNo: undefined, // undefined, not null
        });
      });
    });

    return {
      rackNumber: '5', // String, not number
      verticals,
      shelves,
      locations,
      gridWidth: 5, // Required property
      gridHeight: 6, // Required property
    };
  }, []);

  // Generate random activity
  const generateActivity = () => {
    const location = mockRackStructure.locations[
      Math.floor(Math.random() * mockRackStructure.locations.length)
    ];
    
    const activityTypes = [
      ActivityType.Pick,
      ActivityType.PutAway,
      ActivityType.Transfer,
      ActivityType.CorrectionIn,
      ActivityType.CorrectionOut,
      ActivityType.Counting,
      ActivityType.Receiving,
      ActivityType.Reservation
    ];
    
    const event: LocationActivityEvent = {
      binCode: location.binCode,
      rackNumber: 5,
      activityType: activityTypes[Math.floor(Math.random() * activityTypes.length)],
      timestamp: new Date().toISOString(),
      operatorId: `OP${Math.floor(Math.random() * 5) + 1}`,
      itemNo: `ITEM${Math.floor(Math.random() * 100)}`,
      quantity: Math.floor(Math.random() * 10) + 1,
      companyNo: '001',
      customerNo: 'TEST',
      warehouseId: 'WH01'
    };

    // Add to cache
    cacheManager.addEvent(event);

    // Add to active activities for live mode
    if (mode === 'live') {
      setActiveActivities(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(event.binCode) || [];
        newMap.set(event.binCode, [...existing, event]);
        return newMap;
      });

      // Clear after animation duration
      setTimeout(() => {
        setActiveActivities(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(event.binCode) || [];
          const filtered = existing.filter(e => e.timestamp !== event.timestamp);
          if (filtered.length > 0) {
            newMap.set(event.binCode, filtered);
          } else {
            newMap.delete(event.binCode);
          }
          return newMap;
        });
      }, 7000); // 5s pulse + 2s fadeout
    }

    console.log('[Fase6Test] Generated activity:', event.binCode, ActivityType[event.activityType]);
  };

  // Generate multiple activities
  const generateMultiple = (count: number) => {
    for (let i = 0; i < count; i++) {
      setTimeout(() => generateActivity(), i * 100);
    }
  };

  // Auto-generate activities
  useEffect(() => {
    if (!autoGenerate) return;

    const interval = setInterval(() => {
      generateActivity();
    }, 2000);

    return () => clearInterval(interval);
  }, [autoGenerate, mode]);

  // Calculate heatmap when in heatmap mode or time window changes
  useEffect(() => {
    if (mode === 'heatmap') {
      const events = cacheManager.queryByTimeWindow(timeWindow).events;
      const allBinCodes = mockRackStructure.locations.map(loc => loc.binCode);
      
      // Update calculator config
      heatmapCalculator.updateConfig({ timeWindow });
      
      // Calculate heatmap
      const data = heatmapCalculator.calculate(events, allBinCodes);
      setHeatmapData(data);
      
      console.log('[Fase6Test] Heatmap calculated:', data.totalEvents, 'events,', data.locationIntensities.size, 'locations');
    }
  }, [mode, timeWindow, cacheManager, heatmapCalculator, mockRackStructure.locations]);

  // Clear cache
  const handleClearCache = () => {
    cacheManager.clear(false);
    setActiveActivities(new Map());
    setHeatmapData(null);
    console.log('[Fase6Test] Cache cleared');
  };

  // Cache stats
  const cacheStats = cacheManager.getStats();

  return (
    <div className="h-screen flex flex-col bg-gray-50 p-4 space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          🔥 Fase 6: Heatmap Visualisering - Testing
        </h1>
        <p className="text-sm text-gray-600">
          Test av HeatmapCalculator, HeatmapRackGrid, HeatmapLegend og ModeToggle komponenter
        </p>
      </div>

      {/* Main content - 2 columns */}
      <div className="flex-1 grid grid-cols-[1fr_350px] gap-4 min-h-0">
        {/* Left: Rack visualization */}
        <div className="flex flex-col space-y-4 min-h-0">
          {/* Mode toggle */}
          <ModeToggle
            mode={mode}
            onModeChange={setMode}
            timeWindow={timeWindow}
            onTimeWindowChange={setTimeWindow}
            autoSwitchEnabled={false}
          />

          {/* Rack grid */}
          <div className="flex-1 bg-white border border-gray-300 rounded-lg p-4 overflow-hidden">
            {mode === 'live' ? (
              <LiveRackGrid
                rackStructure={mockRackStructure}
                activeActivities={activeActivities}
                enableTransition={true}
                animationSpeed="normal"
              />
            ) : (
              <HeatmapRackGrid
                rackStructure={mockRackStructure}
                locationIntensities={heatmapData?.locationIntensities || new Map()}
                colorScheme={colorScheme}
                showIntensityValues={true}
                enableTransition={true}
              />
            )}
          </div>

          {/* Legend (only in heatmap mode) */}
          {mode === 'heatmap' && heatmapData && (
            <HeatmapLegend
              colorScheme={colorScheme}
              onColorSchemeChange={setColorScheme}
              maxIntensity={heatmapData.maxIntensity}
              minIntensity={heatmapData.minIntensity}
              averageIntensity={heatmapData.averageIntensity}
              totalEvents={heatmapData.totalEvents}
            />
          )}
        </div>

        {/* Right: Controls and stats */}
        <div className="flex flex-col space-y-4 overflow-y-auto">
          {/* Controls */}
          <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-gray-800 border-b pb-2">Kontroller</h3>

            <button
              onClick={() => generateActivity()}
              className="w-full flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Zap className="w-4 h-4" />
              <span>Generer Aktivitet</span>
            </button>

            <button
              onClick={() => generateMultiple(10)}
              className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              <span>Generer 10 Aktiviteter</span>
            </button>

            <button
              onClick={() => setAutoGenerate(!autoGenerate)}
              className={`w-full px-4 py-2 rounded-lg transition-colors ${
                autoGenerate
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
              }`}
            >
              {autoGenerate ? 'Stopp Auto-generering' : 'Start Auto-generering (2s)'}
            </button>

            <button
              onClick={handleClearCache}
              className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Nullstill Cache</span>
            </button>
          </div>

          {/* Cache statistics */}
          <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm space-y-2">
            <h3 className="text-sm font-bold text-gray-800 border-b pb-2">Cache Statistikk</h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Totalt events:</span>
                <span className="font-semibold">{cacheStats.totalEvents}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cache bruk:</span>
                <span className="font-semibold">{cacheStats.usagePercentage.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Lagt til (lifetime):</span>
                <span className="font-semibold">{cacheStats.totalEventsAdded}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Evicted (lifetime):</span>
                <span className="font-semibold">{cacheStats.totalEventsEvicted}</span>
              </div>
            </div>
          </div>

          {/* Heatmap statistics (only in heatmap mode) */}
          {mode === 'heatmap' && heatmapData && (
            <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm space-y-2">
              <h3 className="text-sm font-bold text-gray-800 border-b pb-2">Heatmap Statistikk</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tidsvindu:</span>
                  <span className="font-semibold">{timeWindow}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total events:</span>
                  <span className="font-semibold">{heatmapData.totalEvents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lokasjoner:</span>
                  <span className="font-semibold">{heatmapData.locationIntensities.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Maks intensitet:</span>
                  <span className="font-semibold text-red-600">{heatmapData.maxIntensity.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Min intensitet:</span>
                  <span className="font-semibold text-blue-600">{heatmapData.minIntensity.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gjennomsnitt:</span>
                  <span className="font-semibold text-orange-600">{heatmapData.averageIntensity.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Mode info */}
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 text-xs text-blue-800">
            <p className="font-semibold mb-1">📝 Modus: {mode === 'live' ? 'LIVE' : 'HEATMAP'}</p>
            <p>
              {mode === 'live' 
                ? 'Viser sanntidsaktivitet med animasjoner. Generer aktiviteter for å se live-oppdateringer.'
                : `Viser aktivitetsintensitet for ${timeWindow}. Generer aktiviteter og bytt til heatmap for å se resultat.`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fase6Test;
